import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtGHS, fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { DispensePanel } from "./DispensePanel";
import { printInpatientBill, printInpatientReceipt } from "./print";
import { LogOut, Printer, Undo2, Wallet } from "lucide-react";
import { toast } from "sonner";

export const IP_STATUS = { admitted: ["pending", "Admitted"], discharged: ["discrepancy", "Discharged – balance due"], settled: ["ok", "Settled"] };

const Stat = ({ k, v, tone = "" }) => (
  <div className="rounded-md border border-border p-2.5"><div className="text-[11px] text-muted-foreground">{k}</div><div className={`font-mono font-semibold ${tone}`} data-testid={`ip-tab-${k.toLowerCase().replace(/\s+/g, "-")}`}>{v}</div></div>
);

const Log = ({ a, canReverse, onReverse }) => {
  const groups = {};
  a.dispenses.forEach((d) => { const k = `${d.at.slice(0, 10)} · ${d.shift_label || "No shift"}`; (groups[k] = groups[k] || []).push(d); });
  if (!a.dispenses.length) return <div className="text-sm text-muted-foreground py-6 text-center">No medicines dispensed yet</div>;
  return Object.entries(groups).reverse().map(([k, ds]) => (
    <div key={k} className="mb-3">
      <div className="text-xs font-semibold text-muted-foreground mb-1">{k}</div>
      <table className="w-full text-sm"><tbody>{ds.map((d) => (
        <tr key={d.id} className={`border-t border-border ${d.reversed ? "opacity-50 line-through" : ""}`} data-testid={`ip-log-${d.id}`}>
          <td className="py-1.5 w-16 text-xs text-muted-foreground">{fmtDateTime(d.at).split(",").pop()}</td>
          <td className="py-1.5">{d.generic_name}<div className="text-[11px] text-muted-foreground no-underline">by {d.staff_name}{d.reversed ? ` · reversed by ${d.reversed_by}: ${d.reverse_reason}` : ""}</div></td>
          <td className="py-1.5 text-right font-mono">{d.quantity} × {fmtGHS(d.unit_price)}</td>
          <td className="py-1.5 text-right font-mono">{fmtGHS(d.line_total)}</td>
          <td className="py-1.5 text-right text-xs">{d.insurance_amount > 0 && <span className="text-sky-700">ins {fmtGHS(d.insurance_amount)}</span>}{d.copay_amount > 0 && <span className="text-amber-600 block">top-up {fmtGHS(d.copay_amount)}</span>}</td>
          <td className="py-1.5 text-right w-24">{canReverse && !d.reversed && a.status === "admitted" && <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => onReverse(d)} data-testid={`ip-reverse-${d.id}`}><Undo2 className="h-3.5 w-3.5 mr-1" />Reverse</Button>}</td>
        </tr>))}</tbody></table>
    </div>
  ));
};

const PayForm = ({ a, onPaid }) => {
  const [method, setMethod] = useState("cash");
  const [amount, setAmount] = useState(String(a.tab.balance));
  const [ref, setRef] = useState("");
  const [busy, setBusy] = useState(false);
  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/inpatients/${a.id}/payments`, { method, amount: Number(amount), reference: ref });
      toast.success(`${data.receipt.receipt_no}: ${fmtGHS(data.receipt.amount)} received`);
      printInpatientReceipt(data, data.receipt); onPaid(data);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-md border border-emerald-500/40 bg-emerald-500/5 p-3 space-y-2" data-testid="ip-pay-form">
      <div className="text-sm font-medium flex items-center gap-1.5"><Wallet className="h-4 w-4" /> Take payment · balance {fmtGHS(a.tab.balance)}</div>
      <div className="flex flex-wrap gap-2">
        <Select value={method} onValueChange={setMethod}><SelectTrigger className="h-9 w-32" data-testid="ip-pay-method"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="cash">Cash</SelectItem><SelectItem value="card">Card</SelectItem><SelectItem value="momo">MoMo</SelectItem></SelectContent></Select>
        <Input type="number" min={0} step="0.01" className="h-9 w-32" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="ip-pay-amount" />
        <Input className="h-9 w-40" placeholder="Reference (optional)" value={ref} onChange={(e) => setRef(e.target.value)} data-testid="ip-pay-ref" />
        <Button disabled={busy || !(Number(amount) > 0)} onClick={pay} data-testid="ip-pay-submit">{busy ? "Saving…" : "Receive & print receipt"}</Button>
      </div>
    </div>
  );
};

export function InpatientRecord({ a, setA, products, onChanged }) {
  const { can } = useAuth();
  const [busy, setBusy] = useState(false);
  if (!a) return null;
  const t = a.tab;
  const upd = (d) => { setA(d); onChanged(); };
  const reverse = async (d) => {
    const reason = window.prompt(`Reason for reversing ${d.quantity} × ${d.generic_name}?`);
    if (!reason || reason.trim().length < 3) return reason !== null && toast.error("Give a reason (at least 3 characters)");
    try { upd((await api.post(`/inpatients/${a.id}/dispenses/${d.id}/reverse`, { reason })).data); toast.success("Dispense reversed — stock returned"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const discharge = async () => {
    if (!window.confirm(`Discharge ${a.patient_name}? No further medicines can be dispensed.`)) return;
    setBusy(true);
    try { const { data } = await api.post(`/inpatients/${a.id}/discharge`); upd(data); toast.success(`Discharged · bill ${data.bill_no}`); printInpatientBill(data); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const [tone, label] = IP_STATUS[a.status] || ["pending", a.status];
  return (
    <Dialog open={!!a} onOpenChange={(o) => !o && setA(null)}>
      <DialogContent className="max-w-none w-screen h-[100dvh] max-h-none rounded-none sm:rounded-none overflow-y-auto content-start p-5 sm:p-8" data-testid="ip-record">
        <DialogHeader><DialogTitle className="flex flex-wrap items-center gap-2">{a.patient_name} <span className="font-mono text-sm text-muted-foreground">File {a.file_no} · {a.code}</span>
          <StatusBadge value={tone} label={label} data-testid="ip-record-status" /></DialogTitle></DialogHeader>
        <div className="text-xs text-muted-foreground">{a.branch_name} · admitted {fmtDateTime(a.admitted_at)} by {a.admitted_by}{a.discharged_at ? ` · discharged ${fmtDateTime(a.discharged_at)}` : ""}
          {" · "}{a.payer_type === "insurance" ? `Insurance: ${a.scheme_name}${a.member_no ? ` (${a.member_no})` : ""}` : a.payer_type === "corporate" ? `Corporate: ${a.customer_name}${a.corporate_member_name ? ` · ${a.corporate_member_name}` : ""}` : "Self-pay"}
          {a.ward && ` · Ward ${a.ward}`}{a.bed && ` · Bed ${a.bed}`}{a.doctor && ` · Dr ${a.doctor}`}</div>
        <div className="grid lg:grid-cols-[1fr_300px] gap-5 mt-3">
          <div className="space-y-4 min-w-0">
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="ip-tab">
              <Stat k="Medicines" v={t.items} /><Stat k="Total charged" v={fmtGHS(t.total)} /><Stat k="Insurance" v={fmtGHS(t.insurance)} tone="text-sky-700" />
              <Stat k="Corporate" v={fmtGHS(t.corporate)} /><Stat k="Patient owes" v={fmtGHS(t.patient)} /><Stat k="Paid" v={fmtGHS(t.paid)} />
              <Stat k="Balance due" v={fmtGHS(t.balance)} tone={t.balance > 0 ? "text-rose-600" : "text-emerald-600"} />
            </div>
            {a.status === "admitted" && can("inpatients.dispense") && <div className="sticky top-0 z-10 bg-background pb-1"><DispensePanel a={a} products={products} onDone={upd} /></div>}
            <div><div className="text-sm font-semibold mb-2">Dispensing log</div><Log a={a} canReverse={can("inpatients.reverse")} onReverse={reverse} /></div>
          </div>
          <div className="space-y-3">
            {a.status === "admitted" && can("inpatients.admit") && <Button className="w-full" variant="outline" disabled={busy} onClick={discharge} data-testid="ip-discharge-btn"><LogOut className="h-4 w-4 mr-1.5" /> Discharge & final bill</Button>}
            {a.status !== "admitted" && <Button className="w-full" variant="outline" onClick={() => printInpatientBill(a)} data-testid="ip-print-bill-btn"><Printer className="h-4 w-4 mr-1.5" /> Print final bill</Button>}
            {a.status === "discharged" && t.balance > 0 && can("inpatients.payments") && <PayForm key={t.paid} a={a} onPaid={upd} />}
            {a.payments.length > 0 && <div className="rounded-md border border-border p-3 text-sm" data-testid="ip-payments">
              <div className="font-medium mb-1">Payments</div>
              {a.payments.map((p) => <div key={p.id} className="flex justify-between text-xs py-0.5"><button className="underline" onClick={() => printInpatientReceipt(a, p)}>{p.receipt_no}</button><span>{p.method}</span><span className="font-mono">{fmtGHS(p.amount)}</span></div>)}
            </div>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
